import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatIDR, formatDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Printer, CheckCircle2, Mail, FileSpreadsheet, Pencil } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PdfPreviewDialog from "../components/PdfPreviewDialog";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [company, setCompany] = useState({ company_name: "PT FUJI INDO SURYA", address: "", phone: "", email: "", tax_id: "", logo_base64: "", bank_accounts: [], signatory_name: "", signatory_title: "Finance Manager" });
  const [sigDlg, setSigDlg] = useState({ open: false, invoice: null, name: "", title: "", city: "Jakarta" });
  const [editDlg, setEditDlg] = useState({ open: false, id: "", invoice_number: "", due_date: "" });
  const [pdfPrev, setPdfPrev] = useState({ open: false, url: "", filename: "", title: "" });

  const openEdit = (inv) => setEditDlg({
    open: true, id: inv.id,
    invoice_number: inv.invoice_number,
    due_date: (inv.due_date || "").slice(0, 10),
  });

  const saveEdit = async () => {
    try {
      await api.put(`/invoices/${editDlg.id}/header`, {
        invoice_number: editDlg.invoice_number,
        due_date: editDlg.due_date,
      });
      toast.success("Nomor invoice & jatuh tempo diperbarui");
      setEditDlg({ ...editDlg, open: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const load = () => Promise.all([
    api.get("/invoices").then((r) => setInvoices(r.data)),
    api.get("/company-profile").then((r) => setCompany({ ...company, ...r.data, bank_accounts: r.data.bank_accounts || [] })).catch(() => {}),
  ]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const markPaid = async (id) => {
    if (!window.confirm("Tandai invoice sebagai LUNAS?")) return;
    try { await api.post(`/invoices/${id}/pay`); toast.success("Invoice lunas — kas masuk & jurnal tercatat"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const sendEmail = async (inv) => {
    try {
      const r = await api.post(`/invoices/${inv.id}/send-email`);
      toast.success(r.data.message || "Email terkirim");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal kirim email"); }
  };

  const exportEFaktur = async (inv) => {
    try {
      const token = localStorage.getItem("erp_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/tax/e-faktur/csv?invoice_id=${inv.id}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        return toast.error(err.detail || "Gagal ekspor e-Faktur");
      }
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `e-faktur_${inv.invoice_number.replaceAll("/", "-")}.csv`;
      link.click();
      toast.success("CSV e-Faktur diunduh");
    } catch (e) { toast.error("Gagal ekspor e-Faktur"); }
  };

  const openPrintDialog = (inv) => {
    setSigDlg({
      open: true,
      invoice: inv,
      name: company.signatory_name || "",
      title: company.signatory_title || "Finance Manager",
      city: "Jakarta",
    });
  };

  const printPDF = (inv, sigOverride = {}, opts = {}) => {
    // A4 = 210 x 297 mm
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const BLUE = [30, 64, 175];       // corporate blue
    const NAVY = [15, 23, 42];
    const SLATE = [100, 116, 139];
    const BORDER = [148, 163, 184];
    const PAGE_W = 210;
    const M = 12; // margin

    // ---- Header block ----
    if (company.logo_base64) {
      try { doc.addImage(company.logo_base64, "PNG", M, 12, 20, 20); } catch (e) {}
    }
    const hx = company.logo_base64 ? M + 24 : M;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...BLUE);
    doc.text((company.company_name || "PT. FUJI INDO SURYA").toUpperCase(), hx, 18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    const addrLines = doc.splitTextToSize(company.address || "", 105);
    let ay = 23;
    addrLines.slice(0, 3).forEach((ln) => { doc.text(ln, hx, ay); ay += 4; });
    if (company.phone) doc.text(`Ph.: ${company.phone}${company.fax ? "  Fax: " + company.fax : ""}`, hx, ay);

    // ---- INVOICE title (right) ----
    doc.setFont("helvetica", "bold");
    doc.setFontSize(28);
    doc.setTextColor(...BLUE);
    doc.text("INVOICE", PAGE_W - M, 20, { align: "right" });

    // ---- Meta table (right side) ----
    const metaTop = 30;
    const metaX = PAGE_W - M - 78;
    const metaValX = PAGE_W - M - 2;
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...NAVY);
    const metaRows = [
      ["INVOICE NO", inv.invoice_number],
      ["PO CUSTOMER", inv.customer_po || "-"],
      ["SHIPMENT", inv.so_number || "-"],
      ["PAYMENT", (company.payment_term_days ? `${company.payment_term_days} Days` : "30 Days")],
      ["INVOICE DATE", formatDate(inv.issue_date)],
      ["DUE DATE", inv.due_date ? formatDate(inv.due_date) : "-"],
    ];
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    let my = metaTop;
    metaRows.forEach(([k, v]) => {
      doc.rect(metaX, my, 78, 5);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...NAVY);
      doc.text(k, metaX + 1.5, my + 3.5);
      doc.setFont("helvetica", "normal");
      doc.text(":", metaX + 30, my + 3.5);
      doc.text(String(v), metaX + 32, my + 3.5);
      my += 5;
    });

    // ---- Divider ----
    let dvY = Math.max(my, 60) + 4;
    doc.setDrawColor(...BLUE);
    doc.setLineWidth(0.5);
    doc.line(M, dvY, PAGE_W - M, dvY);

    // ---- MESSRS block ----
    dvY += 5;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...NAVY);
    doc.text("MESSRS. :", M, dvY);
    dvY += 5;
    doc.setFontSize(10);
    doc.text((inv.customer_name || "-").toUpperCase(), M, dvY);
    dvY += 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(60, 60, 60);
    if (inv.customer_address) {
      const cl = doc.splitTextToSize(inv.customer_address, 130);
      cl.slice(0, 3).forEach((ln) => { doc.text(ln, M, dvY); dvY += 4; });
    }
    if (inv.customer_phone) { doc.text(`TELP: ${inv.customer_phone}`, M, dvY); dvY += 4; }
    if (inv.customer_pic) { doc.setFont("helvetica", "bold"); doc.text(`Attn: ${inv.customer_pic}`, M, dvY); dvY += 4; doc.setFont("helvetica", "normal"); }

    // ---- Items table ----
    const startY = dvY + 4;
    autoTable(doc, {
      startY,
      head: [["DESCRIPTION", "Qty", "Unit", "Price", "Amount (IDR)"]],
      body: inv.items.map((it) => [
        it.name,
        String(it.quantity),
        it.unit || "PCS",
        formatIDR(it.price),
        formatIDR(it.quantity * it.price),
      ]),
      headStyles: { fillColor: BLUE, textColor: [255, 255, 255], fontSize: 9, halign: "center", fontStyle: "bold" },
      bodyStyles: { fontSize: 9, textColor: NAVY, lineColor: BORDER, lineWidth: 0.1 },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { halign: "right", cellWidth: 18 },
        2: { halign: "center", cellWidth: 16 },
        3: { halign: "right", cellWidth: 30 },
        4: { halign: "right", cellWidth: 34, fontStyle: "bold" },
      },
      margin: { left: M, right: M },
      theme: "grid",
    });

    // ---- Financial summary ----
    let fy = doc.lastAutoTable.finalY;
    const sumX = PAGE_W - M - 78;
    const dpp = inv.subtotal * (11 / 12); // DPP Nilai Lain (11/12 dari harga sblm pajak) untuk PPN 12%
    const summary = [
      ["Total", formatIDR(inv.subtotal)],
      ["DPP Nilai Lain (11/12)", formatIDR(Math.round(dpp))],
      ["VAT 12%", formatIDR(inv.ppn)],
      ["Grand Total", formatIDR(inv.total)],
    ];
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    summary.forEach(([k, v], i) => {
      const isTotal = i === summary.length - 1;
      if (isTotal) {
        doc.setFillColor(...BLUE);
        doc.rect(sumX, fy, 78, 7, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(k, sumX + 2, fy + 4.8);
        doc.text(v, sumX + 76, fy + 4.8, { align: "right" });
        fy += 7;
      } else {
        doc.rect(sumX, fy, 78, 5);
        doc.setTextColor(...NAVY);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(k, sumX + 2, fy + 3.5);
        doc.text(v, sumX + 76, fy + 3.5, { align: "right" });
        fy += 5;
      }
    });

    // ---- Bank account block (left) ----
    let bY = doc.lastAutoTable.finalY + 4;
    const bank = (company.bank_accounts && company.bank_accounts[0]) || {};
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...NAVY);
    doc.text("ACCOUNT DETAILS", M, bY);
    doc.setDrawColor(...BLUE);
    doc.setLineWidth(0.4);
    doc.line(M, bY + 1, M + 40, bY + 1);
    bY += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text(`Bank Name    : ${bank.bank_name || "-"}`, M, bY); bY += 4.5;
    doc.text(`Account Name : ${bank.account_holder || company.company_name || "-"}`, M, bY); bY += 4.5;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...NAVY);
    doc.text(`Account No.  : ${bank.account_number || "-"}`, M, bY);

    // ---- Signature block (right) — bisa di-override per print ----
    const sigX = PAGE_W - M - 60;
    const sigY = Math.max(fy, bY) + 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...NAVY);
    const sigCity = sigOverride.city || "Jakarta";
    const jakartaDate = `${sigCity}, ${formatDate(inv.issue_date)}`;
    doc.text(jakartaDate, sigX + 30, sigY, { align: "center" });
    doc.setFont("helvetica", "bold");
    const sName = sigOverride.name || company.signatory_name || "";
    const sTitle = sigOverride.title || company.signatory_title || "Finance Manager";
    doc.text(sName ? sName.toUpperCase() : "____________________", sigX + 30, sigY + 22, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...SLATE);
    doc.text(sTitle, sigX + 30, sigY + 26, { align: "center" });

    // ---- Footer note ----
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.text(`Diterbitkan otomatis oleh ${company.company_name || "FUJI INDO SURYA ERP SYSTEM"}.`, M, 290);

    const filename = `${inv.invoice_number.replaceAll("/", "-")}.pdf`;
    if (opts.preview) return doc.output("bloburl");
    doc.save(filename);
  };

  const previewInvoice = (inv, sigOverride = {}) => {
    try {
      const url = printPDF(inv, sigOverride, { preview: true });
      if (url) setPdfPrev({ open: true, url, filename: `${inv.invoice_number.replaceAll("/", "-")}.pdf`, title: `Preview Invoice ${inv.invoice_number}` });
    } catch (e) { toast.error("Gagal preview: " + e.message); }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div>
        <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Modul Invoice</div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">Invoice Penjualan</h1>
        <p className="text-sm text-slate-500 mt-1">Nomor invoice, prefix, dan termin bayar bisa diubah di <a href="/settings" className="underline text-navy-900 hover:text-yellow-700">Profil Perusahaan</a>. Per invoice juga bisa di-edit lewat tombol ✎.</p>
      </div>
      <Card className="card-elevated"><CardContent className="p-4">
        <div className="overflow-x-auto">
          <Table data-testid="invoices-table">
            <TableHeader>
              <TableRow>
                <TableHead>No. Invoice</TableHead>
                <TableHead>No. PO Customer</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Terbit</TableHead><TableHead>Jatuh Tempo</TableHead>
                <TableHead className="text-right">Subtotal</TableHead><TableHead className="text-right">PPN</TableHead>
                <TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow key={inv.id} data-testid={`invoice-row-${inv.invoice_number}`}>
                  <TableCell className="font-mono text-xs font-semibold text-navy-900">{inv.invoice_number}</TableCell>
                  <TableCell className="font-mono text-xs">{inv.customer_po || <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell>{inv.customer_name}</TableCell>
                  <TableCell className="text-sm">{formatDate(inv.issue_date)}</TableCell>
                  <TableCell className="text-sm">{inv.due_date ? formatDate(inv.due_date) : "-"}</TableCell>
                  <TableCell className="text-right font-mono">{formatIDR(inv.subtotal)}</TableCell>
                  <TableCell className="text-right font-mono">{formatIDR(inv.ppn)}</TableCell>
                  <TableCell className="text-right font-mono font-bold">{formatIDR(inv.total)}</TableCell>
                  <TableCell>
                    {inv.status === "paid"
                      ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">LUNAS</Badge>
                      : <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100">UNPAID</Badge>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(inv)} data-testid={`invoice-edit-${inv.invoice_number}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => openPrintDialog(inv)} data-testid={`invoice-pdf-${inv.invoice_number}`}><Printer size={14} className="mr-1" />PDF</Button>
                    <Button size="sm" variant="outline" className="ml-2" onClick={() => sendEmail(inv)} data-testid={`invoice-email-${inv.invoice_number}`}><Mail size={14} className="mr-1" />Email</Button>
                    {inv.ppn > 0 && <Button size="sm" variant="outline" className="ml-2" onClick={() => exportEFaktur(inv)} data-testid={`invoice-efaktur-${inv.invoice_number}`}><FileSpreadsheet size={14} className="mr-1" />e-Faktur</Button>}
                    {inv.status === "unpaid" && (
                      <Button size="sm" className="ml-2 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => markPaid(inv.id)} data-testid={`invoice-pay-${inv.invoice_number}`}>
                        <CheckCircle2 size={14} className="mr-1" />Lunas
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {invoices.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Belum ada invoice</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      {/* Signatory dialog — pilih nama & jabatan penandatangan per invoice */}
      <Dialog open={sigDlg.open} onOpenChange={(v) => setSigDlg({ ...sigDlg, open: v })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Cetak Invoice — Pilih Penandatangan</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2">
              Nama & jabatan default berasal dari <strong>Profil Perusahaan</strong>. Anda bisa mengubahnya hanya untuk cetakan ini (tidak tersimpan permanen).
            </div>
            <div>
              <Label>Kota Tanda Tangan</Label>
              <Input value={sigDlg.city} onChange={(e) => setSigDlg({ ...sigDlg, city: e.target.value })} placeholder="Jakarta" data-testid="sig-city-input" />
            </div>
            <div>
              <Label>Nama Penandatangan</Label>
              <Input value={sigDlg.name} onChange={(e) => setSigDlg({ ...sigDlg, name: e.target.value })} placeholder="Cth: Kartika Sari" data-testid="sig-name-input" />
            </div>
            <div>
              <Label>Jabatan</Label>
              <Input value={sigDlg.title} onChange={(e) => setSigDlg({ ...sigDlg, title: e.target.value })} placeholder="Cth: Finance Manager" data-testid="sig-title-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSigDlg({ ...sigDlg, open: false })}>Batal</Button>
            <Button className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="sig-print-btn" onClick={() => {
              previewInvoice(sigDlg.invoice, { name: sigDlg.name, title: sigDlg.title, city: sigDlg.city });
              setSigDlg({ ...sigDlg, open: false });
            }}><Printer size={14} className="mr-1" />Preview PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit invoice header (nomor & jatuh tempo) */}
      <Dialog open={editDlg.open} onOpenChange={(v) => setEditDlg({ ...editDlg, open: v })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Edit Nomor & Jatuh Tempo Invoice</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2">
              Ubah nomor invoice atau geser tanggal jatuh tempo bila perlu. Perubahan akan tersimpan di jurnal.
            </div>
            <div>
              <Label>Nomor Invoice</Label>
              <Input value={editDlg.invoice_number} onChange={(e) => setEditDlg({ ...editDlg, invoice_number: e.target.value })} className="font-mono" data-testid="edit-inv-number-input" />
            </div>
            <div>
              <Label>Tanggal Jatuh Tempo</Label>
              <Input type="date" value={editDlg.due_date} onChange={(e) => setEditDlg({ ...editDlg, due_date: e.target.value })} data-testid="edit-inv-due-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDlg({ ...editDlg, open: false })}>Batal</Button>
            <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={saveEdit} data-testid="edit-inv-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PdfPreviewDialog open={pdfPrev.open} url={pdfPrev.url} filename={pdfPrev.filename} title={pdfPrev.title} onClose={() => setPdfPrev({ open: false, url: "", filename: "", title: "" })} />
    </div>
  );
}